<template>
  <a-modal v-model:visible="visible" :title="title" :footer="null" :afterClose="closeModal" width="55vw">
    <div class="detailModal" v-if="record && record.name">
      <div><label>处方名称：</label><span>{{ record.name }}</span></div>
      <div><label>处方描述：</label><span>{{ record.description }}</span></div>
    </div>

    <a-table class="ant-table-striped" :columns="columns" :data-source="data" rowKey="id" :pagination="false"
      bordered>
      <template #title>
        <div class="thead">{{ isDisease ? "已关联疾病" : "已关联症候群" }}</div>
      </template>
      <template #name="{ record: row }">
        <div style="text-align: left;">{{ nameOf(row.diseaseId) }}</div>
      </template>
      <template #operation="{ record: row }">
        <div class="flex_sa">
          <div class="editable-row-operations">
            <span>
              <a-popconfirm title="是否确认取消关联" @confirm="onDelete(row)" okText="确认" cancelText="取消">
                <a>删除</a>
              </a-popconfirm>
            </span>
          </div>
        </div>
      </template>
    </a-table>

    <div style="text-align: center; margin-top: 20px;">
      <span style="font-weight: bold;">新增关联：</span>
      <a-select v-model:value="selectedId" showSearch placeholder="请选择" optionFilterProp="label"
        style="width: 240px; margin-right: 10px;">
        <a-select-option v-for="item in options" :value="item.id" :key="item.id" :label="item.name">
          {{ item.name }}
        </a-select-option>
      </a-select>
      <a-button type="primary" @click="add">添加</a-button>
    </div>
  </a-modal>
</template>

<script>
import { Detail, Add, Delete } from "/src/api/autoPrescription.js";
import { Search as DiseaseSearch } from "/src/api/disease.js";
import { Search as SyndromeSearch } from "/src/api/syndrome.js";

// 处方管理页筛选的类型 → 关联表(tcm/prescription/ai)里的 category
const CATEGORY_MAP = {
  Jingfang: { disease: "Jingfang", syndrome: null },
  Mifang: { disease: "Mifang", syndrome: null },
  药对: { disease: null, syndrome: "药对" },
  穴位处方: { disease: "疾病穴位处方", syndrome: "症候穴位处方" },
};

// store 里的 diseaseList/syndromeList 只含 isStandard=true 的数据，
// 关联表若指向非标数据或已删除的数据（悬空关联），名称会退化成原始数字。
// 这里按需拉一次全量列表建 id→名称 映射（模块级缓存，弹窗反复打开只拉一次）
const fullNameMaps = { disease: null, syndrome: null };
async function getFullNameMap(kind) {
  if (fullNameMaps[kind]) return fullNameMaps[kind];
  try {
    const res =
      kind === "disease"
        ? await DiseaseSearch({ pageSize: -1 })
        : await SyndromeSearch({ pageSize: -1 });
    const data = res.errCode == 0 ? res.data : null;
    const rows = Array.isArray(data) ? data : (data && data.records) || [];
    const map = {};
    rows.forEach((r) => {
      if (r && r.id != null) map[r.id] = r.name;
    });
    fullNameMaps[kind] = map;
  } catch (e) {
    fullNameMaps[kind] = {};
  }
  return fullNameMaps[kind];
}

export default {
  name: "关联疾病症候群",
  emits: ["update:modelValue", "reload"],
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    // 当前处方行
    record: {
      type: Object,
      default: () => ({}),
    },
    // disease | syndrome
    kind: {
      type: String,
      default: "disease",
    },
  },
  data() {
    return {
      visible: this.modelValue,
      data: [],
      selectedId: undefined,
      // store/全量列表都查不到的 id → 回显文本（悬空关联标注已删除）
      nameFallback: {},
    };
  },
  computed: {
    isDisease() {
      return this.kind === "disease";
    },
    // 关联表里用的 category（后端 ai/list 的 category 参数）
    aiCategory() {
      const map = CATEGORY_MAP[this.record?.category];
      return map ? map[this.kind] : null;
    },
    title() {
      const label = this.isDisease ? "关联疾病" : "关联症候群";
      return this.record?.name ? `${label}：${this.record.name}` : label;
    },
    // 可选列表：疾病取 diseaseList，症候群取 syndromeList（store 登录后已加载）
    sourceList() {
      const list = this.isDisease
        ? this.$store.state.diseaseList
        : this.$store.state.syndromeList;
      return Array.isArray(list) ? list : [];
    },
    options() {
      // 列表很长（疾病 400+ 条），只渲染前 500 条够用
      return this.sourceList.slice(0, 500);
    },
    columns() {
      return [
        {
          title: "名称",
          key: "name",
          dataIndex: "name",
          width: "70%",
          slots: {
            customRender: "name",
          },
        },
        {
          title: "操作",
          dataIndex: "operation",
          width: "30%",
          slots: {
            customRender: "operation",
          },
        },
      ];
    },
  },
  mounted() {
    // 保险：两个基础列表若没加载过则补一次
    if (!this.$store.state.diseaseList?.length) {
      this.$store.dispatch("getDiseaseList");
    }
    if (!this.$store.state.syndromeList?.length) {
      this.$store.dispatch("getSyndromeList");
    }
  },
  methods: {
    nameOf(diseaseId) {
      const item = this.sourceList.find((x) => x.id == diseaseId);
      if (item) return item.name;
      // store 列表（isStandard=true）查不到：①非标数据 → 全量列表补查；②已删除（悬空关联）→ 标注
      const hit = this.nameFallback[diseaseId];
      if (hit !== undefined) return hit;
      getFullNameMap(this.isDisease ? "disease" : "syndrome").then((map) => {
        this.nameFallback = {
          ...this.nameFallback,
          [diseaseId]: map[diseaseId] || `（ID:${diseaseId} 已删除）`,
        };
      });
      return `（ID:${diseaseId} 查询中…）`;
    },
    async detail() {
      if (!this.record?.id) {
        this.data = [];
        return;
      }
      // 后端新口径（2026-10-10）：ai/list 只需 prescriptionId 即可按处方反查，不再要求 category。
      // 返回的是该处方的全部关联（疾病+症候群混在一起），前端按当前列的类别过滤：
      // 疾病列取疾病类关联、症候群列取症候类关联；经方/秘方没有症候群关联口径（aiCategory 为 null），显示为空。
      let res = await Detail({
        prescriptionId: this.record.id,
      });
      if (res.errCode == 0) {
        const rows = res.data || [];
        this.data = this.aiCategory
          ? rows.filter((row) => row.category === this.aiCategory)
          : [];
      } else {
        this.$message.error("查询失败", function () { });
      }
    },
    async add() {
      if (!this.selectedId) {
        this.$message.info("请先选择要关联的" + (this.isDisease ? "疾病" : "症候群"), function () { });
        return;
      }
      if (this.data.find((item) => item.diseaseId == this.selectedId)) {
        this.$message.info("该关联已存在", function () { });
        return;
      }
      // 后端要求（2026-10-12）：新建关联时把名称一并传入，补齐 ai 表的 diseaseName 字段
      //（批量导入的老数据都带，之前弹窗新建的行缺这个字段）
      const selectedItem = this.sourceList.find((x) => x.id == this.selectedId);
      await Add({
        prescriptionId: this.record.id,
        category: this.aiCategory,
        diseaseId: this.selectedId,
        diseaseName: selectedItem ? selectedItem.name : "",
      });
      this.selectedId = undefined;
      this.detail();
      this.$emit("reload");
    },
    async onDelete(row) {
      await Delete({ id: row.id });
      this.detail();
      this.$emit("reload");
    },
    closeModal() {
      this.$emit("update:modelValue", false);
      this.selectedId = undefined;
    },
  },
  watch: {
    // 注意：不要 watch record —— 页面 openLink() 里是先换 record 再开弹窗，
    // 两个 watch 会先后触发 detail()，导致 ai/list 被请求两次；打开时机统一交给 modelValue
    modelValue(val) {
      this.visible = val;
      if (val) {
        this.selectedId = undefined;
        this.detail();
      }
    },
  },
};
</script>

<style scoped>
.thead {
  text-align: center;
  font-weight: bold;
  letter-spacing: 0.5em;
}
</style>
